import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Eye, MapPin, Clock, DollarSign } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Badge } from '../../../components/ui/badge';
import { AppLayout } from '../../../components/layout/AppLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MyJobsPage = () => {
  const navigate = useNavigate();
  const [postedJobs, setPostedJobs] = useState([]);
  const [acceptedJobs, setAcceptedJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllJobs();
  }, []);

  const fetchAllJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch jobs I posted (as customer)
      const postedResponse = await axios.get(`${API_URL}/api/pickup/my-deliveries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPostedJobs(postedResponse.data.deliveries || []);

      // Fetch jobs I accepted (as runner)
      const acceptedResponse = await axios.get(`${API_URL}/api/pickup/my-jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAcceptedJobs(acceptedResponse.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      posted: { label: 'Posted', variant: 'default', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
      matched: { label: 'Matched', variant: 'secondary', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
      in_progress: { label: 'In Progress', variant: 'secondary', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
      on_the_way: { label: 'On the way', variant: 'secondary', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
      ready_for_pickup: { label: 'Ready for pickup', variant: 'secondary', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
      delivered: { label: 'Delivered', variant: 'outline', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' },
      completed: { label: 'Completed', variant: 'outline', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      cancelled: { label: 'Cancelled', variant: 'destructive', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
      disputed: { label: 'Disputed', variant: 'outline', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' },
      
      // Legacy statuses (backward compatibility)
      accepted: { label: 'Matched', variant: 'secondary', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
    };
    const config = statusConfig[status] || statusConfig.posted;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const JobCard = ({ job, type }) => (
    <div 
      onClick={() => navigate(`/pickup/delivery/${job.id}`)}
      className="bg-white dark:bg-gray-900 p-4 rounded-xl border-2 hover:border-blue-500 transition-all cursor-pointer hover:shadow-lg"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {getStatusBadge(job.status)}
            {type === 'accepted' && (
              <Badge variant="outline" className="bg-green-50 text-green-700">
                Runner Job
              </Badge>
            )}
          </div>
          <h3 className="font-semibold text-lg line-clamp-1">
            {job.task_description || job.package_description || 'Job request'}
          </h3>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-green-600">
            {job.currency} {job.delivery_fee}
          </p>
        </div>
      </div>

      <div className="space-y-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 flex-shrink-0" />
          <span className="line-clamp-1">
            {job.pickup_area}, {job.pickup_city} → {job.destination_area}, {job.destination_city}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 flex-shrink-0" />
          <span>{job.pickup_date}</span>
        </div>
      </div>

      {job.offers_count > 0 && job.status === 'posted' && (
        <div className="mt-3 text-sm text-blue-600 dark:text-blue-400">
          💬 {job.offers_count} {job.offers_count === 1 ? 'offer' : 'offers'} received
        </div>
      )}

      <Button size="sm" variant="outline" className="w-full mt-3">
        <Eye className="h-4 w-4 mr-2" />
        View Details
      </Button>
    </div>
  );

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b sticky top-0 z-10 px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pickup')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Jobs
              </h1>
              <p className="text-sm text-muted-foreground">
                Jobs you posted and jobs you're working on
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <Tabs defaultValue="posted" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="posted">
                Jobs I Posted ({postedJobs.length})
              </TabsTrigger>
              <TabsTrigger value="accepted">
                Jobs I'm Doing ({acceptedJobs.length})
              </TabsTrigger>
            </TabsList>

            {/* Posted Jobs Tab */}
            <TabsContent value="posted" className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                </div>
              ) : postedJobs.length === 0 ? (
                <div className="text-center py-12 space-y-4">
                  <Package className="h-16 w-16 mx-auto text-muted-foreground/50" />
                  <div>
                    <p className="text-muted-foreground font-medium">No posted jobs yet</p>
                    <p className="text-sm text-muted-foreground">Post a job to get started</p>
                  </div>
                  <Button onClick={() => navigate('/pickup/post')}>
                    Post a Job
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {postedJobs.map(job => (
                    <JobCard key={job.id} job={job} type="posted" />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Accepted Jobs Tab */}
            <TabsContent value="accepted" className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                </div>
              ) : acceptedJobs.length === 0 ? (
                <div className="text-center py-12 space-y-4">
                  <Package className="h-16 w-16 mx-auto text-muted-foreground/50" />
                  <div>
                    <p className="text-muted-foreground font-medium">No accepted jobs yet</p>
                    <p className="text-sm text-muted-foreground">Browse available jobs to start earning</p>
                  </div>
                  <Button onClick={() => navigate('/pickup/browse')}>
                    Browse Jobs
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {acceptedJobs.map(job => (
                    <JobCard key={job.id} job={job} type="accepted" />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppLayout>
  );
};

export default MyJobsPage;
